import { FC, useState, useCallback, ReactNode, useRef, useEffect } from 'react'
import { CardNumberElementProps, CardExpiryElementProps, CardCvcElementProps } from '@stripe/react-stripe-js'
import {
  StripeCardNumberElementOptions,
  StripeCardNumberElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardCvcElementChangeEvent,
} from '@stripe/stripe-js'
import { Omit } from 'interfaces'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import { InputLayout } from 'components/atoms/inputs/InputLayout/InputLayout'

const OPTIONS: StripeCardNumberElementOptions = {
  style: {
    base: {
      fontFamily: 'Graphik, sans-serif',
      fontSize: '18px',
      color: '#242b42',
    },
    invalid: {
      color: '#242b42',
    },
  },
}

type ElementProps =
  | CardNumberElementProps
  | CardExpiryElementProps
  | CardCvcElementProps

type PartialComponent =
  & Partial<Omit<ElementProps, 'options'>>
  & {
    options?: Partial<Pick<NonNullable<ElementProps['options']>, 'placeholder'>>
  }

type StripeChangeEvent =
  | StripeCardNumberElementChangeEvent
  | StripeCardExpiryElementChangeEvent
  | StripeCardCvcElementChangeEvent

type InputDecoratedWithStripeState = {
  hasFocus: boolean
  hasValue: boolean
}

type InputDecoratedWithStripeProps = {
  label: ReactNode
  component: FC<PartialComponent>
  isReady?: boolean
  className?: string
  onEscape?: () => void
  onReady?: () => void
}
export const InputDecoratedWithStripe: FC<InputDecoratedWithStripeProps> = (props) => {
  const { component: Component, label, isReady = true, onEscape, onReady, className } = props

  // Sometimes Stripe fires events after component was unmount :(
  const isMountRef = useRef(true)
  useEffect(() => {
    isMountRef.current = true
    return () => {
      isMountRef.current = false
    }
  }, [])

  const [error, setError] = useState<string| undefined>(undefined)
  const [state, setState] = useState<InputDecoratedWithStripeState>({
    hasFocus: false,
    hasValue: false,
  })

  const handleFocus = useCallback(() => {
    if (!isMountRef.current) {
      return
    }

    setState({
      ...state,
      hasFocus: true,
    })
  }, [state])

  const handleBlur = useCallback(() => {
    if (!isMountRef.current) {
      return
    }

    setState({
      ...state,
      hasFocus: false,
    })

  }, [state])

  const handleChanged = useCallback<(event: StripeChangeEvent) => void>((event) => {
    if (!isMountRef.current) {
      return
    }

    setState({
      ...state,
      hasValue: !event.empty,
    })

    setError(event.error ? event.error.message : undefined)
  }, [state])

  return (
    <InputLayout
      { ...state }
      error={ error }
      label={ label }
      className={ className }
      themeStyle='underlined'
      dynamicHeight={ false }
    >
      { !isReady && <Spinner size='medium' /> }
      <Component
        onFocus={ handleFocus }
        onBlur={ handleBlur }
        onEscape={ onEscape }
        onChange={ handleChanged }
        onReady={ onReady }
        options={ OPTIONS }
      />
    </InputLayout>
  )
}
